import React from 'react';

export interface MenuSection {
  title: string;
  links: Array<{
    text: string;
    href: string;
  }>;
}

interface ContainerWithSidebarProps {
  menuSections: MenuSection[];
  children: React.ReactNode;
  mainTitle?: string;
  className?: string;
}

function ArrowIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      className="au-text-black au-ml-2"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.55554 1.65625L10.8991 5.99985M10.8991 5.99985L6.55554 10.3434M10.8991 5.99985H1.10059"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ContainerWithSidebar({
  menuSections,
  children,
  mainTitle,
  className = '',
}: ContainerWithSidebarProps) {
  return (
    <div className={`au-container ${className}`}>
      <div className="au-grid lg:au-grid-cols-[21rem_1fr] au-gap-6">
        <div>
          <aside className="lg:au-sticky lg:au-top-[120px] md:au-mb-8 md:au-relative">
            {menuSections.map(section => (
              <div key={section.title} className="au-mb-12 sidebar-menu">
                <h2 className="au-text-[21px] au-font-bold au-mb-6">
                  {section.title}
                </h2>
                <ul className="au-m-0 au-p-0 au-list-none">
                  {section.links.map(link => (
                    <li
                      key={link.text}
                      className="au-border-b au-border-gray-200 last:au-border-b-0"
                    >
                      <a
                        href={link.href}
                        className="au-flex au-items-center au-justify-between au-py-3 au-text-black hover:au-text-blue-600 au-no-underline au-transition-colors group"
                      >
                        <span className="au-text-md">{link.text}</span>
                        <span className="group-hover:au-translate-x-1 au-transition-transform">
                          <ArrowIcon />
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </aside>
        </div>

        <div className="au-max-w-full lg:au-pl-[6%]">
          {mainTitle && (
            <h2 className="au-text-4xl au-font-bold au-mb-8">{mainTitle}</h2>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

ContainerWithSidebar.attributes = {
  menuSections: {
    type: 'array',
    items: {
      type: 'object',
      properties: {
        title: { type: 'string' },
        links: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              text: { type: 'string' },
              href: { type: 'string' },
            },
            required: ['text', 'href'],
          },
        },
      },
      required: ['title', 'links'],
    },
  },
  mainTitle: { type: 'string' },
  children: { type: 'string' },
  className: { type: 'string' },
};

export default ContainerWithSidebar;
